import React from 'react'
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  MenuItem,
  Typography,
  CircularProgress
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import CustomDialog from 'common/components/CustomDialog'
import { useForm, Controller } from 'react-hook-form'
import { useFarms } from '../../../farm/api/queries'
import { useGetCyclesByFarmByPond, useGetPondsByFarm } from '../../../pond/api/queries'
import * as yup
from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useSaveSearchHistory } from 'modules/comparisonPond/api/mutation'
import { formatThaiShortDateTwoDigitYear } from 'common/utils/date'
import { selectedCycleIdsState } from 'modules/comparisonPond/stores/atom'
import { useRecoilState } from 'recoil'
import { useSearchParams } from 'react-router-dom'

type Props = {
  open: boolean
  onClose: () => void
}

type FormValues = {
  farm: string
  pond: string
  cycle: string
}

const AddCompareDialog: React.FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation('pondComparison');

  const [searchParams, setSearchParams] = useSearchParams();
  const idParam = searchParams.get('cycleIds');

  const { mutate: saveSearchHistory } = useSaveSearchHistory();

  const schema = yup.object({
    farm: yup.string().required(t('requiredFarm')),
    pond: yup.string().required(t('requiredPond')),
    cycle: yup.string().required(t('requiredCycle'))
  }).required()

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    clearErrors,
    setError,
    formState: { isValid, errors },
  } = useForm<FormValues>({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: { farm: '', pond: '', cycle: '' },
  })

  const selectedFarm = watch('farm')
  const selectedPond = watch('pond')

  const { data: farms, isLoading: farmsLoading } = useFarms()
  const { data: ponds, isLoading: pondsLoading } = useGetPondsByFarm(selectedFarm)
  const { data: cycles, isLoading: cyclesLoading } = useGetCyclesByFarmByPond(selectedFarm, selectedPond)

  const handleConfirm = (values: FormValues) => {

    let hasError = false;
    if (!values.farm) {
      setError('farm', { type: 'manual', message: t('requiredFarm') });
      hasError = true;
    }
    if (values.farm && !values.pond) {
      setError('pond', { type: 'manual', message: t('requiredPond') });
      hasError = true;
    }
    if (values.pond && !values.cycle) {
      setError('cycle', { type: 'manual', message: t('requiredCycle') });
      hasError = true;
    }
    if (hasError) return;

    saveSearchHistory({
      cycle_id: values.cycle,
    })

    const params = new URLSearchParams(searchParams);
    const existing = params.get('cycleIds');
    const arr = existing ? existing.split(',').filter(Boolean) : [];
    if (!arr.includes(String(values.cycle))) {
      arr.push(String(values.cycle));
    }
    if (arr.length) params.set('cycleIds', arr.join(','));
    else params.delete('cycleIds');
    setSearchParams(params);
    handleClose()
  }

  const handleClose = () => {
    onClose()
    reset({ farm: '', pond: '', cycle: '' })
  }

  return (
    <CustomDialog
      title={t('addCompare')}
      open={open}
      closeDialog={handleClose}
      fullWidth
      disabledSubmit={!isValid || farmsLoading || pondsLoading || cyclesLoading}
      onSubmit={() => {
        handleSubmit(handleConfirm)()
      }}
    >
      <form onSubmit={handleSubmit(handleConfirm)} id="add-compare-form">
        <Box display="grid" gap={2} mt={1}>
          <Typography component="div">
            <Typography component="span" color="error" sx={{ mr: 0.5 }}>*</Typography>
            {t('farmLabel')}
          </Typography>

          <Controller
            name="farm"
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                value={field.value}
                onChange={(e) => {
                  field.onChange(e.target.value)
                  setValue('pond', '')
                  setValue('cycle', '')
                  clearErrors(['pond', 'cycle'])
                }}
                placeholder={t('selectFarm')}
                fullWidth
                select
                size="small"
                error={!!fieldState.error}
                helperText={fieldState.error ? fieldState.error.message : ''}
              >
                <MenuItem value="">{t('selectFarm')}</MenuItem>
                {farmsLoading ? (
                  <MenuItem value="" disabled>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={16} />
                      <span>{t('loading')}</span>
                    </Box>
                  </MenuItem>
                ) : (
                  Array.isArray(farms?.farms) && farms?.farms.map((f: any) => (
                    <MenuItem key={String(f.id ?? f.value ?? f)} value={String(f.id ?? f.value ?? f)}>
                      {f.name ?? f.label ?? String(f)}
                    </MenuItem>
                  ))
                )}
              </TextField>
            )}
          />

          <Typography component="div">
            <Typography component="span" color="error" sx={{ mr: 0.5 }}>*</Typography>
            {t('pondLabel')}
          </Typography>
          <Controller
            name="pond"
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                value={field.value}
                onChange={(e) => {
                  field.onChange(e.target.value)
                  setValue('cycle', '')
                  clearErrors('cycle')
                }}
                placeholder={t('selectPond')}
                fullWidth
                select
                size="small"
                disabled={!selectedFarm || pondsLoading}
                error={!!fieldState.error}
                helperText={
                  !selectedFarm ? t('chooseFarmFirst')
                    : pondsLoading ? t('loadingPonds')
                      : (fieldState.error ? fieldState.error.message : '')
                }
              >
                <MenuItem value="">{t('selectPond')}</MenuItem>
                {pondsLoading ? (
                  <MenuItem value="" disabled>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={16} />
                      <span>{t('loading')}</span>
                    </Box>
                  </MenuItem>
                ) : (
                  Array.isArray(ponds) && ponds.map((p: any) => (
                    <MenuItem key={String(p.id ?? p.value ?? p)} value={String(p.id ?? p.value ?? p)}>
                      {p.name ?? p.label ?? String(p)}
                    </MenuItem>
                  ))
                )}
              </TextField>
            )}
          />

          <Typography component="div">
            <Typography component="span" color="error" sx={{ mr: 0.5 }}>*</Typography>
            {t('cycleLabel')}
          </Typography>
          <Controller
            name="cycle"
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                placeholder={t('selectCycle')}
                fullWidth
                select
                size="small"
                disabled={!selectedPond || cyclesLoading}
                error={!!fieldState.error}
                helperText={!selectedPond ? t('choosePondFirst') : (fieldState.error ? fieldState.error.message : '')}
              >
                <MenuItem value="">{t('selectCycle')}</MenuItem>
                {cyclesLoading ? (
                  <MenuItem value="" disabled>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={16} />
                      <span>{t('loading')}</span>
                    </Box>
                  </MenuItem>
                ) : (
                  Array.isArray(cycles) && cycles.map((c: any) => (
                    <MenuItem key={String(c.id ?? c.value ?? c)} value={String(c.id ?? c.value ?? c)}>
                      {formatThaiShortDateTwoDigitYear(c.startDate)} - {c.endDate ? formatThaiShortDateTwoDigitYear(c.endDate) : t('present')}
                    </MenuItem>
                  ))
                )}
              </TextField>
            )}
          />
        </Box>
      </form>
    </CustomDialog>
  )
}

export default AddCompareDialog